/**
 * PreviewWeightCells — read-only current → next + Δ columns.
 *
 * A row a weight rule touched marks its NEXT value with a pin (static) or a
 * spark (dynamic boost); the title spells out the rule, the shared total and
 * any fit adjustment so the number is never a mystery.
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC } from 'react';
import { ArrowRight, Pin, Zap } from 'lucide-react';

import { cn } from '@/lib/utils';

import DeltaPill from './DeltaPill';
import type { PreviewRow } from './preview-row.types';

export interface PreviewWeightCellsProps {
  row: PreviewRow;
}

const PreviewWeightCells: FC<PreviewWeightCellsProps> = ({ row }) => (
  <>
    <span className="text-right text-[11px] tabular-nums text-muted-foreground">
      {fmtPct(row.currentPerMille)}
    </span>
    <ArrowRight className="h-3 w-3 justify-self-center text-muted-foreground/60" aria-hidden />
    <span
      title={weightRuleTitle(row)}
      className={cn(
        'inline-flex items-center justify-end gap-0.5 text-right text-[11px] font-semibold tabular-nums',
        row.passThrough ? 'text-muted-foreground' : 'text-primary',
        row.weightRule?.adjusted && 'text-warning',
      )}
    >
      {row.weightRule ? (
        row.weightRule.mode === 'static' ? (
          <Pin className="h-2.5 w-2.5 shrink-0" aria-label="Pinned by a weight rule" />
        ) : (
          <Zap className="h-2.5 w-2.5 shrink-0" aria-label="Boosted by a weight rule" />
        )
      ) : null}
      {fmtPct(row.optimalPerMille)}
    </span>
    <div className="flex justify-end">
      <DeltaPill deltaPerMille={row.deltaPerMille} />
    </div>
  </>
);

function weightRuleTitle(row: PreviewRow): string | undefined {
  const rule = row.weightRule;
  if (!rule) return undefined;
  const name = rule.ruleName ? ` “${rule.ruleName}”` : '';
  const tail = rule.adjusted ? ' Adjusted to fit 100%.' : '';
  if (rule.mode === 'static') {
    return `Pinned by a weight rule${name} — its matches share ${rule.percent}% of the pool, split by score.${tail}`;
  }
  const appliedPerMille = rule.appliedPerMille ?? 0;
  if (appliedPerMille === 0 && rule.adjusted) {
    return `A weight rule${name} tried to boost this, but the pool had no room to give — nothing was added.`;
  }
  const applied = (appliedPerMille / 10).toFixed(1);
  return `Boosted by a weight rule${name} — its matches share +${rule.percent} points; this one gets +${applied}%.${tail}`;
}

function fmtPct(perMille: number): string {
  return `${(perMille / 10).toFixed(perMille >= 100 ? 1 : 2)}%`;
}

export default PreviewWeightCells;
